import { create } from 'zustand'

/**
 * Cart line item shape: { productId, productName, productSlug, productImage, variantName, unitPrice, quantity }
 * variantName is null when product has no variant selected.
 */
export const useCartStore = create((set) => ({
  items: [],

  addItem: (product, variant, qty = 1) => {
    const variantName = variant ?? null
    const productId = product.id
    const quantity = Math.max(1, Math.floor(Number(qty)) || 1)
    const unitPrice = Number(product.price)
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return

    set((state) => {
      const existing = state.items.findIndex(
        (i) => i.productId === productId && (i.variantName ?? null) === variantName
      )
      let next = [...state.items]
      if (existing >= 0) {
        next[existing] = { ...next[existing], quantity: next[existing].quantity + quantity }
      } else {
        next.push({
          productId,
          productName: product.name ?? '',
          productSlug: product.slug ?? '',
          productImage: product.main_image ?? null,
          variantName,
          unitPrice,
          quantity,
        })
      }
      return { items: next }
    })
  },

  removeItem: (productId, variantName) => {
    set((state) => ({
      items: state.items.filter(
        (i) => i.productId !== productId || (i.variantName ?? null) !== (variantName ?? null)
      ),
    }))
  },

  updateQty: (productId, variantName, qty) => {
    const quantity = Math.max(0, Math.floor(Number(qty)) || 0)
    set((state) => {
      if (quantity === 0) {
        return {
          items: state.items.filter(
            (i) => i.productId !== productId || (i.variantName ?? null) !== (variantName ?? null)
          ),
        }
      }
      const next = state.items.map((i) =>
        i.productId === productId && (i.variantName ?? null) === (variantName ?? null)
          ? { ...i, quantity }
          : i
      )
      return { items: next }
    })
  },

  clearCart: () => set({ items: [] }),
}))

// Selectors for derived state (Zustand doesn't support getters on the store object; use selectors)
export const selectTotalItems = (state) =>
  state.items.reduce((sum, i) => sum + i.quantity, 0)
export const selectTotalPrice = (state) =>
  state.items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0)
